// Unpublished Work © 2023-2024 Deere & Company.

import { AxiosResponse } from "axios";
import {
  OrderReviewResponse2,
  OrderUpdateResponse,
} from "../../pages/orders/types";
import { LegacyInventoryDetail } from "../../pages/parts/PartInfo/types";
import { BlobResponse } from "../../utils/downloadFile";
import axios from "../api";
import {
  CountriesAndRegions,
  OrderSummaryPDFRequest,
  UpdateShipToAddressResponse,
  UpdateShipToAddressV2Response,
  UserPreferenceResponse,
} from "./xtremeBffInterfaces";
import { DealerAccount, DealerDiscount } from "../dealer/types";
import { OrderProcessRequest, SavedAddress } from "../orders/orderInterfaces";
import { BffRBACRolesResponse } from "../rbac/types";
import {
  LineValidationRequest,
  LineValidationResponse,
} from "../validation/validationInterfaces";

const xtremeBffApi = {
  lineValidation: (
    xtremeBffServiceURL: string,
    lineValidationRequest: LineValidationRequest,
    accessToken: string
  ): Promise<LineValidationResponse> =>
    axios
      .postWithRetries(
        `${xtremeBffServiceURL}/validate/line`,
        { ...lineValidationRequest },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  linesValidation: (
    xtremeBffServiceURL: string,
    linesValidationRequest: LineValidationRequest[],
    accessToken: string,
    breakOnFailure?: boolean,
    lang?: string
  ): Promise<Record<string, LineValidationResponse>> =>
    axios
      .postWithRetries(
        `${xtremeBffServiceURL}/validate/lines`,
        [...linesValidationRequest],
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { lang, breakOnFailure },
        }
      )
      .then((response) => {
        const responseData: Record<string, LineValidationResponse> =
          Object.entries(response.data).reduce((acc, [key, value]) => {
            const partNumberMatch = key.match(/partNumber=([^,]+)/);
            const requestedQuantityMatch = key.match(
              /requestedQuantity=([^,]+)/
            );
            const partNumber = partNumberMatch ? partNumberMatch[1] : "";
            const requestedQuantity = requestedQuantityMatch
              ? requestedQuantityMatch[1]
              : "0";

            const newKey = `${partNumber}-${requestedQuantity}`;
            acc[newKey] = <LineValidationResponse>value;
            return acc;
          }, {} as Record<string, LineValidationResponse>);

        return responseData;
      })
      .catch((error) => Promise.reject(error)),
  getCountriesAndRegions: (
    xtremeBffServiceURL: string,
    accessToken: string
  ): Promise<CountriesAndRegions> =>
    axios
      .getWithRetries(`${xtremeBffServiceURL}/configs/countriesAndRegions`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),
  updateShipToAddress: (
    xtremeBffServiceURL: string,
    accessToken: string,
    shipToAddress: SavedAddress,
    orderNumber: string,
    accountId: string,
    lang?: string
  ): Promise<UpdateShipToAddressResponse> =>
    axios
      .patchWithRetries(
        `${xtremeBffServiceURL}/orders/updateShipToAddress/${orderNumber}`,
        shipToAddress,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { accountId, lang },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  updateShipToAddressWithCleanser: (
    xtremeBffServiceURL: string,
    accessToken: string,
    shipToAddress: SavedAddress,
    orderNumber: string,
    accountId: string,
    lang?: string
  ): Promise<UpdateShipToAddressResponse> =>
    axios
      .patchWithRetries(
        `${xtremeBffServiceURL}/orders/updateShipToAddressWithCleanser/${orderNumber}`,
        shipToAddress,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { accountId, lang },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),
  updateShipToAddressV2: (
    xtremeBffServiceURL: string,
    accessToken: string,
    shipToAddress: SavedAddress,
    orderNumber: string,
    accountId: string,
    lang?: string,
    selectedFromSuggestion?: boolean,
    userConfirmed?: boolean
  ): Promise<UpdateShipToAddressV2Response> =>
    axios
      .patchWithRetries(
        `${xtremeBffServiceURL}/orders/v2/updateShipToAddress/${orderNumber}`,
        shipToAddress,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { accountId, lang, selectedFromSuggestion, userConfirmed },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  async getAccountsDetails(
    xtremeBffServiceURL: string,
    accountIds: string[],
    accessToken: string,
    language: string
  ): Promise<DealerAccount[]> {
    const { data } = await axios.getWithRetries<DealerAccount[]>(
      `${xtremeBffServiceURL}/dealer/accounts?accountIds=${accountIds.join(
        ","
      )}&lang=${language}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    return data;
  },

  downloadOrderSummary: (
    xtremeBffServiceURL: string,
    accessToken: string,
    orderSummaryRequest: OrderSummaryPDFRequest
  ): Promise<AxiosResponse<BlobResponse>> =>
    axios
      .postWithRetries(
        `${xtremeBffServiceURL}/orderSummary/pdf`,
        orderSummaryRequest,
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/pdf",
          },
        }
      )
      .then((response) => response)
      .catch((error: any) => Promise.reject(error)),

  getLegacyInventoryDetails: (
    xtremeBffServiceURL: string,
    accountId: string,
    accessToken: string,
    partNumber: string,
    lang: string
  ): Promise<LegacyInventoryDetail> =>
    axios
      .getWithRetries(
        `${xtremeBffServiceURL}/inventory/legacyInventoryDetails`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            accountId,
            partNumber,
            lang,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  getUserPreferences: (
    xtremeBffServiceURL: string,
    accessToken: string,
    userXid: string,
    accountId?: string,
    mappedAccountIds?: string[]
  ): Promise<UserPreferenceResponse> =>
    axios
      .postWithRetries(
        `${xtremeBffServiceURL}/preferences/userPreferences`,
        {
          userXid,
          accountId,
          mappedAccountIds,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  getShippingAddresses: (
    xtremeBffServiceURL: string,
    accessToken: string,
    accountId: string,
    orderType?: string
  ): Promise<SavedAddress[]> => {
    const params = new URLSearchParams({ accountId });
    if (orderType) params.set("orderType", orderType);
    return axios
      .getWithRetries<SavedAddress[]>(
        `${xtremeBffServiceURL}/dealer/shippingAddress?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error));
  },
  updateOrder: async (
    xtremeBffServiceURL: string,
    accessToken: string,
    payload: OrderProcessRequest,
    lang: string
  ): Promise<OrderUpdateResponse> =>
    axios
      .postWithRetries<OrderUpdateResponse>(
        `${xtremeBffServiceURL}/orders/process`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            userAction: "update",
            client: "UI",
            lang,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  reviewOrder: async (
    xtremeBffServiceURL: string,
    accessToken: string,
    payload: OrderProcessRequest,
    lang: string
  ): Promise<OrderReviewResponse2> =>
    axios
      .postWithRetries<OrderReviewResponse2>(
        `${xtremeBffServiceURL}/orders/process`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            userAction: "review",
            client: "UI",
            lang,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  showRPM: (
    xtremeBffServiceURL: string,
    accessToken: string,
    accountID: string
  ): Promise<boolean> =>
    axios
      .getWithRetries(
        `${xtremeBffServiceURL}/rpm/show-rpm?accountID=${accountID}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )
      .then((response) => response.data)
      .catch((error) => Promise.reject(error)),

  getDealerSelectionDiscountsSet: (
    xtremeBffServiceURL: string,
    accessToken: string,
    accountId: string
  ): Promise<DealerDiscount[]> =>
    axios
      .getWithRetries<DealerDiscount[]>(
        `${xtremeBffServiceURL}/dealer/selectionDiscountsSet`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { accountId },
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        console.log("Error fetching dealer selection discounts set:", error);
        return [];
      }),
  getRbacMappings: (
    xtremeBffServiceURL: string,
    accessToken: string,
    idToken: string,
    delegatedUserId?: string
  ): Promise<BffRBACRolesResponse> =>
    axios
      .getWithRetries<BffRBACRolesResponse>(
        `${xtremeBffServiceURL}/rbac/info`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "x-id-token": idToken,
          },
          ...(delegatedUserId && { params: { userId: delegatedUserId } }),
        }
      )
      .then((response) => response.data)
      .catch((error) => {
        console.log("Error fetching RBAC mappings:", error);
        return { userId: "", roles: [], businessActivities: [] };
      }),
};

export default xtremeBffApi;
